/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import type { DeployNewReleaseActionResource, EnvironmentResource, LifecycleResource, PhaseResource } from "@octopusdeploy/octopus-server-client";
import * as _ from "lodash";
import { useCallback, useMemo } from "react";
import * as React from "react";
import { EnvironmentChip, ChipIcon } from "~/components/Chips";
import LookupResourceChipComponent from "~/components/LookupResourceChip";
import { ExpandableFormSection, Summary } from "~/components/form";
interface DeployNewReleaseActionEditorProps {
    action: DeployNewReleaseActionResource;
    allEnvironments: EnvironmentResource[];
    lifecycle: LifecycleResource;
    onActionChange(action: DeployNewReleaseActionResource): void;
}
const LookupEnvironmentChip = LookupResourceChipComponent<EnvironmentResource>();
export const DeployNewReleaseActionEditor: React.FC<DeployNewReleaseActionEditorProps> = ({ action, allEnvironments, lifecycle, onActionChange }) => {
    const environmentId = useMemo(() => action.EnvironmentId, [action]);
    const phaseEnvironments = React.useMemo(() => {
        const environmentNameMap: Record<string, string> = {};
        allEnvironments.forEach((env) => {
            environmentNameMap[env.Id] = env.Name;
        });
        return getEnvironmentsWithSimulatedReleaseProgression(lifecycle.Phases).map((e) => ({ value: e, text: environmentNameMap[e] }));
    }, [allEnvironments, lifecycle.Phases]);
    const onDestinationEnvironmentChange = useCallback((destinationEnvironmentId: string | undefined) => {
        onActionChange({
            ...action,
            EnvironmentId: destinationEnvironmentId!,
        });
    }, [action, onActionChange]);
    const destinationEnvironmentSummary = useMemo(() => environmentId
        ? Summary.summary(<span>
                          New release will be deployed to the{" "}
                          {<LookupEnvironmentChip lookupCollection={allEnvironments} key={environmentId} lookupId={environmentId} type={ChipIcon.Environment} chipRender={(item) => <EnvironmentChip environmentName={item.Name}/>}/>} environment
                      </span>)
        : Summary.placeholder("No destination environment selected"), [environmentId, allEnvironments]);
    return (<div>
            <ExpandableFormSection errorKey="DestinationEnvironment" title="Destination environment" focusOnExpandAll summary={destinationEnvironmentSummary} help="The environment to deploy the created release to.">
                <DeprecatedSelect items={phaseEnvironments} value={environmentId} onChange={onDestinationEnvironmentChange} sortItems={false}/>
            </ExpandableFormSection>
        </div>);
};
DeployNewReleaseActionEditor.displayName = "DeployNewReleaseActionEditor"
const getEnvironmentsWithSimulatedReleaseProgression = (phases: PhaseResource[]): string[] => {
    const firstPhase = phases[0];
    // This can happen if the project has no environments or phases defined
    if (!firstPhase) {
        return [];
    }
    // If the first phase is not optional, only allow environments from the first phase
    if (!firstPhase.IsOptionalPhase) {
        return [...firstPhase.AutomaticDeploymentTargets, ...firstPhase.OptionalDeploymentTargets];
    }
    // If the first phase is optional, walk through subsequent phases until hitting a non-optional one
    const remainingPhases: PhaseResource[] = [];
    for (const p of phases.slice(1)) {
        // Walk the phases till we get to the non-optional one, include it in the list
        remainingPhases.push(p);
        if (!p.IsOptionalPhase) {
            break;
        }
    }
    return _.flatten([
        // Include the first phase (which is optional)
        ...firstPhase.AutomaticDeploymentTargets,
        ...firstPhase.OptionalDeploymentTargets,
        // Include subsequent optional phases and the first non-optional phase
        ...remainingPhases.map((p) => [...p.AutomaticDeploymentTargets, ...p.OptionalDeploymentTargets]),
    ]);
};
